import React from 'react';

import { UNDO_SECONDS } from '../../../hooks/usePulseEventMachine';
import { type ICaptureState, PU_EVENT_LABELS, type PuEventType } from '../../../interface/pu-events';
import { CheckSVG } from '../../utils/svgs';

interface ICaptureButtonProps {
	eventType: PuEventType;
	captureState: ICaptureState;
	onTap: (eventType: PuEventType) => void;
	onUndo: () => void;
	onRetry: () => void;
	onDismiss: () => void;
	/** Compact variant for the overview widget. */
	compact?: boolean;
}

const RING_RADIUS = 16;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/**
 * The one-tap capture CTA with the 10-second undo grace ring. Only ever
 * rendered for ONE event at a time (the slot it belongs to); the machine's
 * phase + eventType decide which visual state this instance shows.
 */
const CaptureButton = ({ eventType, captureState, onTap, onUndo, onRetry, onDismiss, compact }: ICaptureButtonProps) => {
	const mine = captureState.eventType === eventType;
	const phase = mine ? captureState.phase : 'idle';
	const busyElsewhere = !mine && captureState.phase !== 'idle';

	if (phase === 'armed') {
		const progress = captureState.undoSecondsLeft / UNDO_SECONDS;
		return (
			<div className={`pue-capture pue-capture--armed ${compact ? 'pue-capture--compact' : ''}`}>
				<span
					className='pue-capture__ring'
					aria-hidden='true'
				>
					<svg
						viewBox='0 0 40 40'
						width='40'
						height='40'
					>
						<circle
							className='pue-capture__ring-track'
							cx='20'
							cy='20'
							r={RING_RADIUS}
						/>
						<circle
							className='pue-capture__ring-fill'
							cx='20'
							cy='20'
							r={RING_RADIUS}
							strokeDasharray={RING_CIRCUMFERENCE}
							strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
						/>
					</svg>
					<span className='pue-capture__ring-count'>{captureState.undoSecondsLeft}</span>
				</span>
				<div className='pue-capture__armed-copy'>
					<p className='pue-capture__armed-title'>Capturing “{PU_EVENT_LABELS[eventType]}”</p>
					<p className='pue-capture__armed-sub'>Sends in {captureState.undoSecondsLeft}s — tap undo if this was a mistake</p>
				</div>
				<button
					type='button'
					className='pue-capture__undo'
					onClick={onUndo}
				>
					Undo
				</button>
			</div>
		);
	}

	if (phase === 'committing') {
		return (
			<div className={`pue-capture pue-capture--busy ${compact ? 'pue-capture--compact' : ''}`}>
				<span
					className='pue-capture__spinner'
					aria-hidden='true'
				/>
				<p className='pue-capture__busy-text'>Recording “{PU_EVENT_LABELS[eventType]}”…</p>
			</div>
		);
	}

	if (phase === 'sent') {
		return (
			<div
				className={`pue-capture pue-capture--sent ${compact ? 'pue-capture--compact' : ''}`}
				role='status'
			>
				<CheckSVG
					width='18'
					height='18'
					color='currentColor'
				/>
				<p className='pue-capture__sent-text'>Captured & recorded</p>
			</div>
		);
	}

	if (phase === 'queued-offline') {
		return (
			<div
				className={`pue-capture pue-capture--queued ${compact ? 'pue-capture--compact' : ''}`}
				role='status'
			>
				<p className='pue-capture__queued-text'>Saved on this device — syncs when you’re back online</p>
			</div>
		);
	}

	if (phase === 'failed') {
		return (
			<div className={`pue-capture pue-capture--failed ${compact ? 'pue-capture--compact' : ''}`}>
				<p className='pue-capture__failed-text'>{captureState.lastError || 'Could not record the event.'}</p>
				<div className='pue-capture__failed-actions'>
					{captureState.lastErrorFinal ? null : (
						<button
							type='button'
							className='pue-capture__retry'
							onClick={onRetry}
						>
							Retry
						</button>
					)}
					<button
						type='button'
						className='pue-capture__dismiss'
						onClick={onDismiss}
					>
						Dismiss
					</button>
				</div>
			</div>
		);
	}

	return (
		<button
			type='button'
			className={`pue-capture pue-capture--idle ${compact ? 'pue-capture--compact' : ''}`}
			onClick={() => onTap(eventType)}
			disabled={busyElsewhere}
		>
			<span
				className='pue-capture__beacon'
				aria-hidden='true'
			/>
			<span className='pue-capture__idle-label'>Tap to capture — {PU_EVENT_LABELS[eventType]}</span>
		</button>
	);
};

export default CaptureButton;
